import { Component, ChangeDetectionStrategy, inject, effect, signal, computed, input, model } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ReactiveFormsModule, FormsModule, FormGroup, FormControl, Validators, FormArray } from "@angular/forms";
import { Store } from "@ngrx/store";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { DynamicDialogRef } from "primeng/dynamicdialog";
import { ButtonModule } from "primeng/button";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { TabsModule } from "primeng/tabs";
import { InputTextModule } from "primeng/inputtext";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { FloatLabelModule } from "primeng/floatlabel";
import { TextareaModule } from "primeng/textarea";
import { ChipModule } from "primeng/chip";

import { Comment, CreateOrder, QuoteStatus } from "src/api-service";
import { strictEmailValidator } from "src/app/shared/validators/validations";
import { selectFinancingCompanies } from "src/app/store/application/data/financing-company/financing-companies/financing-companies.selectors";
import { selectIsSubscriptionQuote, selectQuoteState } from "src/app/store/pages/quote/quote.selectors";
import { selectVersion } from "src/app/store/pages/quote/version/version.selectors";
import { selectCreditCheckEnabled, selectQuoteCreditCheck, selectQuoteTotalIncVat } from 'src/app/store/application/data/credit-check/quote/quote-credit-check.selectors';
import { QuoteCreditCheckStatusComponent } from 'src/app/pages/quote/components/quote-credit-check-status/quote-credit-check-status.component';
import { selectUserContext } from "src/app/store/user/user.selectors";
import { QuoteActions } from "src/app/store/pages/quote/quote.actions";
import { CreateOrderDialogCommentsComponent } from "./create-order-dialog-comments/create-order-dialog-comments.component";
import { CoreErrorsComponent } from "src/app/shared/components/core-errors/core-errors.component";
import { CreateOrderSummaryComponent } from "../create-order-summary/create-order-summary.component";
import { DividerModule } from "primeng/divider";
import { CoreCurrencyPipe } from "src/app/shared/pipe/core-currency.pipe";

@Component({
    selector: "app-create-order-dialog",
    templateUrl: "./create-order-dialog.component.html",
    styleUrls: ["./create-order-dialog.component.scss"],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        FormsModule,
        TranslateModule,
        CoreErrorsComponent,
        ButtonModule,
        ToggleSwitchModule,
        TabsModule,
        InputTextModule,
        InputGroupModule,
        InputGroupAddonModule,
        FloatLabelModule,
        TextareaModule,
        ChipModule,
        CreateOrderDialogCommentsComponent,
        CreateOrderSummaryComponent,
        DividerModule,
        QuoteCreditCheckStatusComponent
    ],
})
export class CreateOrderDialogComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    public readonly ref = inject(DynamicDialogRef, { optional: true });

    /**
     * True when rendered as a panel inside a flow stepper rather than as a standalone dialog.
     * The stepper then owns navigation and closing, so this component hides its own footer and
     * stops closing the (absent) dialog ref.
     */
    public readonly embedded = input(false);

    private readonly store = inject(Store);
    private readonly translateService = inject(TranslateService);

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    private readonly quoteSignal = toSignal(this.store.select(selectQuoteState));
    private readonly versionSignal = toSignal(this.store.select(selectVersion));
    private readonly userSignal = toSignal(this.store.select(selectUserContext));
    private readonly creditCheckEnabled = toSignal(this.store.select(selectCreditCheckEnabled), { initialValue: false });
    public readonly isSubscription = toSignal(this.store.select(selectIsSubscriptionQuote), { initialValue: false });
    private readonly financingCompanies = toSignal(this.store.select(selectFinancingCompanies), { initialValue: [] });

    // =========================================================================
    // COMPUTED
    // =========================================================================
    public readonly quote = computed(() => this.quoteSignal());

    public readonly selectedVersion = computed(() => this.versionSignal());

    public readonly comments = computed<Comment[]>(() => {
        const quote = this.quoteSignal();
        if (!quote?.comments) return [];

        return [...quote.comments].sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
    });

    public readonly hasComments = computed(() => this.comments().length > 0);

    /**
     * The manual "I certify a credit check was done" toggle.
     *
     * Only where the real credit-check flow is unavailable: no configured provider for the company's
     * country, or a foreign customer (see `selectCreditCheckEnabled`). Where it IS available, the
     * step dialog shown before this one is the
     * attestation — the "Nej, jag bekräftar att kreditkontrollen redan är gjord" button — so
     * asking again here would be duplicate friction.
     */
    public readonly showCreditCheck = computed(() => {
        const quote = this.quoteSignal();
        if (!quote) return false;
        if (this.creditCheckEnabled()) return false;
        return quote.deliveryInfo?.paymentTerm?.requiresCreditCheck === true;
    });

    public readonly isUnderReview = computed(() => {
        const quote = this.quoteSignal();
        return quote?.status === QuoteStatus.UnderReview;
    });

    public readonly creditCheck = toSignal(this.store.select(selectQuoteCreditCheck));
    public readonly quoteTotalIncVat = toSignal(this.store.select(selectQuoteTotalIncVat), { initialValue: 0 });

    /**
     * Whether the confirmation tab has anything to render. Every block on it is conditional, so
     * without this check the panel can come up completely blank — which it did for Swedish quotes
     * once the manual credit-check toggle moved into the step dialog.
     */
    public readonly hasConfirmationContent = computed(() =>
        this.isUnderReview()
        || this.hasComments()
        || this.showCreditCheck()
        || !!this.creditCheck(),
    );

    /**
     * Whether the confirmation panel has anything at all to render.
     *
     * Every block on it is conditional, and the summaries moved to the bottom of the dialog, so a
     * Sent quote with no comments and no credit check leaves it genuinely empty. Rendering the tab
     * shell anyway produced a tall band of padding under a "Bekräftelse" label — better to show
     * nothing than a heading over nothing.
     */
    public readonly hasPanelContent = computed(() =>
        this.isSubscription()
            // The subscription branch is comments-only; everything else it used to show now lives
            // in the summary at the bottom of the dialog.
            ? this.hasComments()
            : this.hasConfirmationContent() || this.hasComments(),
    );

    public readonly canConfirm = computed(() => {
        // Subscription orders: only the comments-confirmation gate applies.
        if (this.isSubscription()) {
            if (this.hasComments() && !this.anyCommentSelected() && !this.commentsOK()) return false;
            return true;
        }

        // Credit check required if shown
        if (this.showCreditCheck() && !this.creditCheckOK()) return false;

        // Comments OK required if there are comments and none selected
        if (this.hasComments() && !this.anyCommentSelected() && !this.commentsOK()) return false;

        // Form valid if sending mail
        // emailFormStatus(), not emailForm.invalid — see the note on send-quote's canSend().
        if (this.sendMail() && this.emailFormStatus() !== "VALID") return false;

        return true;
    });

    public readonly anyCommentSelected = computed(() => this.commentsToInclude().some(x => x));

    public readonly selectedCommentsCount = computed(() => this.commentsToInclude().filter(x => x).length);

    // =========================================================================
    // LOCAL STATE
    // =========================================================================
    public readonly activeTab = signal("0");
    public readonly creditCheckOK = model(false);
    public readonly commentsOK = signal(false);
    public readonly sendMail = signal(false);
    public readonly showCommentField = signal(false);
    public readonly commentsToInclude = signal<boolean[]>([]);
    public validCcEmails: string[] = [];
    public ccEmailControl = new FormControl("", strictEmailValidator());

    // =========================================================================
    // FORM
    // =========================================================================
    public readonly emailForm = new FormGroup({
        salesSupportPerson: new FormControl({}, Validators.required),
        recipientEmail: new FormControl("", [Validators.required, strictEmailValidator()]),
        ccEmails: new FormArray([]),
        message: new FormControl({ value: "", disabled: true }, Validators.required),
        sendMail: new FormControl(false),
        showCommentField: new FormControl(false),
        comment: new FormControl(""),
    });

    /** Reactive mirror of the email form's validity. */
    private readonly emailFormStatus = toSignal(this.emailForm.statusChanges, { initialValue: this.emailForm.status });


    // =========================================================================
    // EFFECTS
    // =========================================================================
    private readonly initEffect = effect(() => {
        const quote = this.quoteSignal();
        if (quote) {
            // Set sendMail default based on status
            const defaultSendMail = quote.status === QuoteStatus.UnderReview;
            this.sendMail.set(defaultSendMail);
            this.emailForm.patchValue({ sendMail: defaultSendMail });

            // Set recipient email and message
            this.emailForm.patchValue({
                recipientEmail: quote.salesInfo.responsibleSalesPerson?.email || "",
                message: this.translateService.instant("CREATE_ORDER.EMAIL.MESSAGE", {
                    quoteId: quote.id,
                    salesSupportName: quote.salesInfo.salesSupportPerson?.name,
                }),
            });

            // Initialize comments array
            const comments = this.comments();
            this.commentsToInclude.set(Array(comments.length).fill(false));
        }
    });

    private readonly sendMailValidatorsEffect = effect(() => {
        const sendMail = this.sendMail();
        const recipientEmail = this.emailForm.get("recipientEmail");
        const message = this.emailForm.get("message");

        if (sendMail) {
            recipientEmail?.setValidators([Validators.required, strictEmailValidator()]);
            message?.setValidators(Validators.required);
        } else {
            recipientEmail?.clearValidators();
            message?.clearValidators();
        }
        recipientEmail?.updateValueAndValidity();
        message?.updateValueAndValidity();
    });

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================
    public onSendMailChange(value: boolean): void {
        this.sendMail.set(value);
        this.emailForm.patchValue({ sendMail: value });
    }

    public onCreditCheckChange(value: boolean): void {
        this.creditCheckOK.set(value);
    }

    public onCommentsOKChange(value: boolean): void {
        this.commentsOK.set(value);
    }

    public onCommentsToIncludeChange(commentsToInclude: boolean[]): void {
        this.commentsToInclude.set([...commentsToInclude]);
        const anySelected = commentsToInclude.some(x => x);
        if (anySelected) {
            this.commentsOK.set(false);
        }
    }

    public onShowCommentFieldChange(value: boolean): void {
        this.showCommentField.set(value);
    }

    public addCcRecipient(): void {
        const email = this.ccEmailControl.value?.trim() ?? "";

        if (!email) {
            return;
        }

        this.ccEmailControl.markAsTouched();

        if (this.ccEmailControl.invalid) {
            return;
        }

        if (this.validCcEmails.includes(email)) {
            this.ccEmailControl.reset();
            return;
        }

        this.validCcEmails.push(email);
        this.ccEmailControl.reset();
    }

    public removeCcRecipient(email: string): void {
        this.validCcEmails = this.validCcEmails.filter(e => e !== email);
    }

    public onConfirm(): void {
        const quote = this.quoteSignal();
        const version = this.selectedVersion();
        if (!quote || !version) return;

        const requestBody = this.buildCreateOrderRequest();
        this.store.dispatch(
            QuoteActions.createOrder({
                versionId: version.id,
                request: requestBody,
                language: this.translateService.defaultLang,
                dialogKey: 'CREATING_ORDER',
            }),
        );
        this.ref?.close();
    }

    public onCancel(): void {
        this.ref?.close();
    }

    // =========================================================================
    // PRIVATE METHODS
    // =========================================================================
    private buildCreateOrderRequest(): CreateOrder {
        const comments = this.comments();
        const commentsToInclude = this.commentsToInclude();
        const commentIdsToInclude: number[] = [];

        for (let i = 0; i < commentsToInclude.length; i++) {
            if (commentsToInclude[i]) {
                commentIdsToInclude.push(comments[i].id);
            }
        }

        const quote = this.quoteSignal();

        return {
            sendMail: this.sendMail(),
            subject: this.translateService.instant("CREATE_ORDER.EMAIL.SUBJECT", {
                quoteId: quote?.id,
            }),
            recipientEmail: this.emailForm.get("recipientEmail")?.value,
            ccEmails: this.validCcEmails,
            message: this.buildEmailBody(
                this.emailForm.get("message")?.value,
                this.emailForm.get("comment")?.value,
                this.userSignal()?.email,
            ),
            comment: this.emailForm.get("comment")?.value,
            commentIdsToInclude,
        } as CreateOrder;
    }

    private buildEmailBody(message: string, comment: string, email: string): string {
        let body = message ?? "";
        if (comment?.length > 0) {
            body += "\n" + this.translateService.instant("COMMENT") + ": " + comment;
        }
        body += "\n\n" + this.translateService.instant("MAIL.SENT_BY") + ": " + email;
        body += "\n" + this.translateService.instant("MAIL.NO_REPLY_INFO");
        return body;
    }
}
